import {
  ATTRIBUTION_ATTRIBUTE_KEY,
  checkoutAttribution,
  serializeAttribution,
} from "@/attribution/attribution";
import { mergeCartAttributes } from "@/cart/cartAttributes";
import type { CartStateGetSet } from "@/cart/types";
import GRAPHQL_CLIENT from "@/graphql/client";
import type { Cart } from "@/graphql/storefront/generated";

/**
 * Reads the live cart: the gift form commits its attributes just before, and
 * the mutation replaces the whole set. Never throws, so checkout always goes.
 */
export const stampAttribution =
  ({ get, set }: CartStateGetSet) =>
  async (): Promise<void> => {
    const { cart } = get();
    if (!cart) return;
    const current = cart.attributes.find(
      ({ key }) => key === ATTRIBUTION_ATTRIBUTE_KEY,
    )?.value;
    const next = serializeAttribution(checkoutAttribution());
    if (next === (current ?? null)) return;
    try {
      const attributes = mergeCartAttributes(
        cart,
        next === null ? {} : { [ATTRIBUTION_ATTRIBUTE_KEY]: next },
      );
      if (next === null) delete attributes[ATTRIBUTION_ATTRIBUTE_KEY];
      const { updateCartAttributes } =
        await GRAPHQL_CLIENT.UpdateCartAndLineAttributes({
          cartId: cart.id,
          cartAttributes: Object.entries(attributes).map(([key, value]) => ({
            key,
            value,
          })),
          lines: [],
        });
      const errors = updateCartAttributes?.userErrors ?? [];
      if (errors.length > 0) {
        throw new Error(errors.map((error) => error.message).join(", "));
      }
      if (updateCartAttributes?.cart) {
        set({ cart: updateCartAttributes.cart as Cart });
      }
    } catch (error) {
      console.error("Failed to stamp attribution on the cart:", error);
    }
  };
